import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Paperclip } from "lucide-react";
import { MessageBubble } from "./MessageBubble";
import { MessageContextMenu } from "./MessageContextMenu";
import { MessageAttachmentUploader } from "./MessageAttachmentUploader";
import { ConversationContext } from "./ConversationContext";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { useToast } from "@/components/ui/use-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Id } from "../../../../../convex/_generated/dataModel";

interface Message {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    avatar?: string;
  };
  timestamp: string;
  isRead: boolean;
  attachments?: {
    name: string;
    url: string;
    type: string;
  }[];
}

interface MessageThreadProps {
  recipient: {
    id: string;
    name: string;
    avatar?: string;
    type: "client" | "team" | "contractor";
  };
  messages: Message[];
  onSendMessage: (content: string, attachments?: File[]) => void;
  reference?: {
    type: "opportunity" | "bid" | "service" | "location" | "task" | "application";
    id: string;
    title: string;
  };
  conversation?: {
    id: Id<"conversations">;
    isRead: boolean;
    isPinned: boolean;
    isArchived: boolean;
  };
  onConversationUpdate?: () => void;
}

export function MessageThread({
  recipient,
  messages,
  onSendMessage,
  reference,
  conversation,
  onConversationUpdate,
}: MessageThreadProps) {
  const [newMessage, setNewMessage] = useState("");
  const [isAttachmentUploaderOpen, setIsAttachmentUploaderOpen] = useState(false);
  const { toast } = useToast();
  const { userId: currentUserId } = useCurrentUser();

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({
      description: "Message copied to clipboard",
      duration: 2000,
    });
  };

  const handleReplyMessage = (content: string) => {
    setNewMessage(`> ${content}\n\n`);
  };

  const handleForwardMessage = () => {
    toast({
      description: "Forward feature coming soon",
      duration: 2000,
    });
  };

  const handleDeleteMessage = () => {
    toast({
      description: "Delete feature coming soon",
      duration: 2000,
    });
  };

  const handleFlagMessage = () => {
    toast({
      description: "Message flagged for follow-up",
      duration: 2000,
    });
  };

  const handleUploadAttachments = (files: File[]) => {
    // In a real implementation, you would upload these files to a server
    // and then attach them to the message
    toast({
      description: `${files.length} file(s) attached`,
      duration: 2000,
    });

    // For now, just send the message with the files
    if (newMessage.trim() || files.length > 0) {
      onSendMessage(newMessage, files);
      setNewMessage("");
    }
  };

  return (
    <Card className="flex h-full w-full max-w-full flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex min-w-0 items-center justify-between gap-3 border-b p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={recipient.avatar} />
            <AvatarFallback className="bg-blue-100 text-blue-600">
              {recipient.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-medium">{recipient.name}</h3>
            <p className="text-sm text-gray-500">
              {recipient.type === "client"
                ? "Client"
                : recipient.type === "team"
                  ? "Team Member"
                  : "Contractor"}
            </p>
          </div>
        </div>
        {conversation && (
          <ConversationActionsMenu
            conversationId={conversation.id}
            isRead={conversation.isRead}
            isPinned={conversation.isPinned}
            isArchived={conversation.isArchived}
            onActionComplete={onConversationUpdate}
          />
        )}
      </div>

      {/* Conversation Context */}
      {reference && <ConversationContext reference={reference} />}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={{
                id: message.id,
                content: message.content,
                timestamp: message.timestamp,
                sender: {
                  id: message.sender.id,
                  name: message.sender.name,
                  avatar: message.sender.avatar,
                },
                attachments: message.attachments?.map((att) => ({
                  name: att.name,
                  url: att.url,
                  type: att.type || "file",
                  size: (att as any).size,
                })),
              }}
              currentUserId={currentUserId || ""}
              showAvatar={true}
              showTimestamp={true}
            >
              <MessageContextMenu
                onCopy={() => handleCopyMessage(message.content)}
                onReply={() => handleReplyMessage(message.content)}
                onForward={handleForwardMessage}
                onDelete={handleDeleteMessage}
                onFlag={handleFlagMessage}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </MessageContextMenu>
            </MessageBubble>
          ))}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-4">
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="min-h-[80px]"
          />
        </div>
        <div className="mt-2 flex justify-between">
          <Button variant="outline" size="sm" onClick={() => setIsAttachmentUploaderOpen(true)}>
            <Paperclip className="mr-2 h-4 w-4" /> Attach
          </Button>
          <Button onClick={handleSendMessage} size="sm">
            <Send className="mr-2 h-4 w-4" /> Send
          </Button>
        </div>
      </div>

      <MessageAttachmentUploader
        isOpen={isAttachmentUploaderOpen}
        onClose={() => setIsAttachmentUploaderOpen(false)}
        onUpload={handleUploadAttachments}
      />
    </Card>
  );
}
